/* Flakecoat theme — theme.js (vanilla, no dependencies)
   Modules: header, drawer, dropdowns, search overlay, sliders, lightbox,
   reveal-on-scroll, back-to-top, cookie banner, quote-request list, quantity,
   product gallery, video poster, marquee pause, form validation hints.
*/
(function () {
  'use strict';
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesignMode = !!window.Shopify && !!window.Shopify.designMode;
  const strings = window.themeStrings || {};

  /* ---------- helpers ---------- */
  function trapFocus(container) {
    const focusables = () => $$('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', container).filter(el => el.offsetParent !== null);
    function onKey(e) {
      if (e.key !== 'Tab') return;
      const list = focusables(); if (!list.length) return;
      const first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  }
  function lockScroll(lock) { document.documentElement.style.overflow = lock ? 'hidden' : ''; }

  /* ---------- header ---------- */
  function initHeader() {
    const wrapper = $('[data-header-wrapper]');
    if (!wrapper) return;
    const setH = () => document.documentElement.style.setProperty('--header-offset', (wrapper.closest('.shopify-section') || wrapper).offsetHeight + 'px');
    let scrolled = null;
    const onScroll = () => { const y = window.scrollY; const next = scrolled ? y > 24 : y > 72; if (next !== scrolled) { scrolled = next; wrapper.classList.toggle('is-scrolled', next); document.documentElement.classList.toggle('is-scrolled-topbar', next); setTimeout(setH, 300); } };
    onScroll(); window.addEventListener('scroll', onScroll, { passive: true });
    setH(); window.addEventListener('resize', setH); wrapper.addEventListener('transitionend', setH);

    // Desktop dropdowns
    $$('[data-dropdown]').forEach(item => {
      const btn = $('[data-dropdown-toggle]', item);
      if (!btn) return;
      const open = (v) => { item.classList.toggle('is-open', v); btn.setAttribute('aria-expanded', String(v)); };
      btn.addEventListener('click', (e) => { e.preventDefault(); const isOpen = item.classList.contains('is-open'); $$('[data-dropdown].is-open').forEach(o => { if (o !== item) { o.classList.remove('is-open'); $('[data-dropdown-toggle]', o).setAttribute('aria-expanded', 'false'); } }); open(!isOpen); });
      item.addEventListener('mouseenter', () => { if (window.matchMedia('(hover:hover)').matches) open(true); });
      item.addEventListener('mouseleave', () => { if (window.matchMedia('(hover:hover)').matches) open(false); });
      item.addEventListener('keydown', (e) => { if (e.key === 'Escape') { open(false); btn.focus(); } });
    });
    // Mega menu rail
    $$('[data-mega]').forEach(mega => {
      const rails = $$('[data-rail]', mega), panels = $$('[data-panel]', mega);
      const show = (id) => { rails.forEach(r => { const on = r.dataset.rail === id; r.classList.toggle('is-active', on); r.setAttribute('aria-selected', String(on)); }); panels.forEach(p => { const on = p.dataset.panel === id; p.classList.toggle('is-active', on); p.hidden = !on; }); };
      rails.forEach(r => { r.addEventListener('mouseenter', () => show(r.dataset.rail)); r.addEventListener('focus', () => show(r.dataset.rail)); r.addEventListener('click', () => show(r.dataset.rail)); });
    });
    document.addEventListener('click', (e) => { if (!e.target.closest('[data-dropdown]')) $$('[data-dropdown].is-open').forEach(o => { o.classList.remove('is-open'); $('[data-dropdown-toggle]', o).setAttribute('aria-expanded', 'false'); }); });
  }

  /* ---------- drawers (mobile menu) ---------- */
  function initDrawers() {
    $$('[data-drawer]').forEach(drawer => {
      const id = drawer.id;
      let lastFocus = null, untrap = null;
      const openDrawer = () => { lastFocus = document.activeElement; drawer.setAttribute('open', ''); lockScroll(true); untrap = trapFocus(drawer); const f = $('button[data-drawer-close]', drawer); if (f) f.focus(); $$(`[aria-controls="${id}"]`).forEach(b => b.setAttribute('aria-expanded', 'true')); };
      const closeDrawer = () => { drawer.removeAttribute('open'); lockScroll(false); if (untrap) untrap(); $$(`[aria-controls="${id}"]`).forEach(b => b.setAttribute('aria-expanded', 'false')); if (lastFocus) lastFocus.focus(); };
      $$(`[aria-controls="${id}"]`).forEach(b => b.addEventListener('click', () => drawer.hasAttribute('open') ? closeDrawer() : openDrawer()));
      $$('[data-drawer-close]', drawer).forEach(b => b.addEventListener('click', closeDrawer));
      drawer.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });
      // accordion submenus
      $$('[data-submenu-toggle]', drawer).forEach(btn => {
        const sub = document.getElementById(btn.getAttribute('aria-controls'));
        btn.addEventListener('click', () => { const ex = btn.getAttribute('aria-expanded') === 'true'; btn.setAttribute('aria-expanded', String(!ex)); if (sub) sub.hidden = ex; });
      });
    });
  }

  /* ---------- search overlay ---------- */
  function initSearch() {
    const overlay = $('[data-search-overlay]'); if (!overlay) return;
    let untrap = null, last = null;
    const open = () => { last = document.activeElement; overlay.setAttribute('open', ''); lockScroll(true); untrap = trapFocus(overlay); const i = $('input[type="search"]', overlay); if (i) i.focus(); };
    const close = () => { overlay.removeAttribute('open'); lockScroll(false); if (untrap) untrap(); if (last) last.focus(); };
    $$('[data-search-open]').forEach(b => b.addEventListener('click', open));
    $$('[data-search-close]', overlay).forEach(b => b.addEventListener('click', close));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    overlay.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }

  /* ---------- sliders (scroll-snap) ---------- */
  function initSliders() {
    $$('[data-slider]').forEach(slider => {
      const track = $('[data-slider-track]', slider); if (!track) return;
      const prev = $('[data-slider-prev]', slider), next = $('[data-slider-next]', slider), dots = $('[data-slider-dots]', slider);
      const slides = $$('.slider__slide', track);
      const autoplay = parseInt(slider.dataset.autoplay || '0', 10);
      let timer = null;
      const slideW = () => (slides[0] ? slides[0].getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) : track.clientWidth);
      const index = () => Math.round(track.scrollLeft / slideW());
      const goTo = (i) => { const n = Math.max(0, Math.min(i, slides.length - 1)); track.scrollTo({ left: n * slideW(), behavior: reduceMotion ? 'auto' : 'smooth' }); };
      const update = () => {
        const i = index(); const maxScroll = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= maxScroll;
        if (dots) $$('button', dots).forEach((d, di) => d.setAttribute('aria-current', String(di === i)));
        slides.forEach((s, si) => s.setAttribute('aria-hidden', String(!(s.getBoundingClientRect().left >= track.getBoundingClientRect().left - 2 && s.getBoundingClientRect().right <= track.getBoundingClientRect().right + 2))));
      };
      if (dots) { slides.forEach((_, i) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'slider__dot'; b.setAttribute('aria-label', (strings.slide || 'Slide') + ' ' + (i + 1)); b.addEventListener('click', () => goTo(i)); dots.appendChild(b); }); }
      if (prev) prev.addEventListener('click', () => goTo(index() - 1));
      if (next) next.addEventListener('click', () => goTo(index() + 1));
      track.addEventListener('scroll', () => { window.requestAnimationFrame(update); }, { passive: true });
      track.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index() + 1); } if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index() - 1); } });
      window.addEventListener('resize', update);
      if (autoplay > 0 && !reduceMotion && !isDesignMode) {
        const start = () => { timer = setInterval(() => { const i = index(); goTo(i >= slides.length - 1 ? 0 : i + 1); }, autoplay * 1000); };
        const stop = () => clearInterval(timer);
        start(); slider.addEventListener('mouseenter', stop); slider.addEventListener('mouseleave', start); slider.addEventListener('focusin', stop); slider.addEventListener('focusout', start);
      }
      update();
    });
  }

  /* ---------- lightbox ---------- */
  function initLightbox() {
    const groups = $$('[data-lightbox-group]'); if (!groups.length) return;
    let dlg = $('#ThemeLightbox');
    if (!dlg) {
      dlg = document.createElement('dialog'); dlg.id = 'ThemeLightbox'; dlg.className = 'lightbox';
      dlg.innerHTML = `<button type="button" class="lightbox__close" data-lb-close aria-label="${strings.close || 'Close'}"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg></button><button type="button" class="lightbox__nav lightbox__nav--prev" data-lb-prev aria-label="${strings.previous || 'Previous'}"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 6l-6 6 6 6"/></svg></button><button type="button" class="lightbox__nav lightbox__nav--next" data-lb-next aria-label="${strings.next || 'Next'}"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"/></svg></button><img alt=""><p class="lightbox__caption"></p>`;
      document.body.appendChild(dlg);
    }
    const img = $('img', dlg), cap = $('.lightbox__caption', dlg);
    let items = [], i = 0;
    const show = (n) => { i = (n + items.length) % items.length; const it = items[i]; img.src = it.dataset.full || it.href; img.alt = it.dataset.alt || ''; cap.textContent = it.dataset.caption || ''; };
    groups.forEach(group => {
      const links = $$('[data-lightbox]', group);
      links.forEach((a, n) => a.addEventListener('click', (e) => { e.preventDefault(); items = links; show(n); if (typeof dlg.showModal === 'function') dlg.showModal(); else window.open(a.href); }));
    });
    $('[data-lb-close]', dlg).addEventListener('click', () => dlg.close());
    $('[data-lb-prev]', dlg).addEventListener('click', () => show(i - 1));
    $('[data-lb-next]', dlg).addEventListener('click', () => show(i + 1));
    dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    dlg.addEventListener('keydown', (e) => { if (e.key === 'ArrowLeft') show(i - 1); if (e.key === 'ArrowRight') show(i + 1); });
  }

  /* ---------- reveal on scroll ---------- */
  function initReveal() {
    const els = $$('.reveal'); if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window) || isDesignMode) { els.forEach(e => e.classList.add('is-visible')); return; }
    // Above-the-fold sections show immediately (no fade on first paint); the rest reveal as they enter the viewport.
    // threshold 0 so very tall sections (e.g. a 90-product collection grid) still trigger.
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const io = new IntersectionObserver((entries) => entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } }), { rootMargin: '0px 0px -10% 0px', threshold: 0 });
    els.forEach(e => { if (e.getBoundingClientRect().top < vh) e.classList.add('is-visible'); else io.observe(e); });
  }

  /* ---------- back to top ---------- */
  function initBackToTop() {
    const b = $('[data-back-to-top]'); if (!b) return;
    const t = () => b.classList.toggle('is-visible', window.scrollY > 600);
    t(); window.addEventListener('scroll', t, { passive: true });
    b.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }));
  }

  /* ---------- cookie banner + consent-gated scripts ---------- */
  function initCookies() {
    const banner = $('[data-cookie-banner]');
    const tpl = $('[data-tracking-scripts]');
    const KEY = 'fc_cookie_consent';
    const loadScripts = () => { if (!tpl || tpl.dataset.loaded) return; tpl.dataset.loaded = '1'; const frag = document.createRange().createContextualFragment(tpl.innerHTML); document.body.appendChild(frag); };
    const setConsent = (v) => { try { localStorage.setItem(KEY, v); } catch (e) { } if (window.Shopify && window.Shopify.customerPrivacy) { try { window.Shopify.customerPrivacy.setTrackingConsent({ analytics: v === 'accepted', marketing: v === 'accepted', preferences: v === 'accepted' }, () => { }); } catch (e) { } } if (v === 'accepted') loadScripts(); };
    let stored = null; try { stored = localStorage.getItem(KEY); } catch (e) { }
    if (!banner) return;
    if (stored === 'accepted') { loadScripts(); return; }
    if (stored === 'declined') return;
    banner.hidden = false;
    $('[data-cookie-accept]', banner).addEventListener('click', () => { setConsent('accepted'); banner.hidden = true; });
    $('[data-cookie-decline]', banner).addEventListener('click', () => { setConsent('declined'); banner.hidden = true; });
  }

  /* ---------- quote request list (localStorage) ---------- */
  const RFQ_KEY = 'fc_rfq_items';
  const rfq = {
    get() { try { return JSON.parse(localStorage.getItem(RFQ_KEY) || '[]'); } catch (e) { return []; } },
    set(items) { try { localStorage.setItem(RFQ_KEY, JSON.stringify(items)); } catch (e) { } document.dispatchEvent(new CustomEvent('rfq:change', { detail: items })); },
    has(handle) { return this.get().some(i => i.handle === handle); },
    add(item) { const items = this.get(); if (!items.some(i => i.handle === item.handle)) items.push(Object.assign({ qty: 1 }, item)); this.set(items); },
    remove(handle) { this.set(this.get().filter(i => i.handle !== handle)); },
    setQty(handle, qty) { const items = this.get(); const it = items.find(i => i.handle === handle); if (it) { it.qty = Math.max(1, Math.min(999, parseInt(qty, 10) || 1)); this.set(items); } },
    clear() { this.set([]); }
  };
  window.themeRFQ = rfq;
  function initRFQ() {
    const updateCounts = () => { const n = rfq.get().length; $$('[data-rfq-count]').forEach(el => { el.textContent = n; el.dataset.count = n; }); };
    updateCounts(); document.addEventListener('rfq:change', updateCounts);
    $$('[data-rfq-add]').forEach(btn => {
      const handle = btn.dataset.rfqAdd;
      const render = () => { const added = rfq.has(handle); btn.classList.toggle('is-added', added); btn.setAttribute('aria-pressed', String(added)); $('[data-rfq-label]', btn).textContent = added ? btn.dataset.labelAdded : btn.dataset.labelAdd; };
      render(); document.addEventListener('rfq:change', render);
      btn.addEventListener('click', () => { if (rfq.has(handle)) rfq.remove(handle); else rfq.add({ handle, title: btn.dataset.title, url: btn.dataset.url, image: btn.dataset.image, sku: btn.dataset.sku || '' }); });
    });
    const updateFab = () => { const n = rfq.get().length; $$('[data-rfq-fab]').forEach(el => { el.dataset.count = n; }); };
    updateFab(); document.addEventListener('rfq:change', updateFab);
    const list = $('[data-rfq-list]');
    if (list) {
      const hidden = $('[data-rfq-hidden]'), empty = $('[data-rfq-empty]'), wrap = $('[data-rfq-table]'), summary = $('[data-rfq-summary]');
      const esc = (t) => String(t || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const render = () => {
        const items = rfq.get(); list.innerHTML = '';
        items.forEach(it => {
          const tr = document.createElement('tr');
          tr.innerHTML = `<td><div class="rfq-table__prod"><span class="rfq-table__img">${it.image ? `<img src="${esc(it.image)}" alt="" loading="lazy" width="56" height="56">` : ''}</span><a class="rfq-table__title" href="${esc(it.url)}">${esc(it.title)}</a></div></td><td class="rfq-table__sku">${esc(it.sku || '—')}</td><td><div class="quantity" data-quantity><button type="button" class="quantity__btn" data-qty-minus aria-label="−">−</button><input class="quantity__input" type="number" min="1" max="999" value="${it.qty || 1}" data-qty="${esc(it.handle)}" aria-label="${esc(it.title)}"><button type="button" class="quantity__btn" data-qty-plus aria-label="+">+</button></div></td><td><button type="button" class="rfq-table__remove" data-remove="${esc(it.handle)}">${strings.remove || 'Remove'}</button></td>`;
          list.appendChild(tr);
        });
        $$('[data-remove]', list).forEach(b => b.addEventListener('click', () => rfq.remove(b.dataset.remove)));
        $$('[data-quantity]', list).forEach(q => { const i = $('input', q); $('[data-qty-minus]', q).addEventListener('click', () => rfq.setQty(i.dataset.qty, (parseInt(i.value, 10) || 1) - 1)); $('[data-qty-plus]', q).addEventListener('click', () => rfq.setQty(i.dataset.qty, (parseInt(i.value, 10) || 1) + 1)); i.addEventListener('change', () => rfq.setQty(i.dataset.qty, i.value)); });
        if (hidden) hidden.value = items.map(i => `${i.qty || 1} × ${i.title}${i.sku ? ' (' + i.sku + ')' : ''} — ${location.origin}${i.url}`).join('\n');
        if (empty) empty.hidden = items.length > 0;
        if (wrap) wrap.hidden = items.length === 0;
        if (summary) summary.textContent = items.length === 1 ? (strings.rfqItemsOne || '1 product selected') : (strings.rfqItemsOther || '[count] products selected').replace('[count]', items.length);
      };
      render(); document.addEventListener('rfq:change', render);
      $$('[data-rfq-clear]').forEach(b => b.addEventListener('click', () => rfq.clear()));
      const form = list.closest('form');
      if (form && new URLSearchParams(location.search).get('contact_posted') === 'true' && form.dataset.clearOnSuccess !== 'false') rfq.clear();
    }
  }

  /* ---------- quantity ---------- */
  function initQuantity() {
    $$('[data-quantity]').forEach(q => { const i = $('input', q); $$('[data-qty-minus]', q).forEach(b => b.addEventListener('click', () => { i.value = Math.max(1, (parseInt(i.value, 10) || 1) - 1); i.dispatchEvent(new Event('change', { bubbles: true })); })); $$('[data-qty-plus]', q).forEach(b => b.addEventListener('click', () => { i.value = (parseInt(i.value, 10) || 1) + 1; i.dispatchEvent(new Event('change', { bubbles: true })); })); });
  }

  /* ---------- product gallery ---------- */
  function initProductGallery() {
    $$('[data-product-gallery]').forEach(g => {
      const main = $('[data-gallery-main] img', g); const thumbs = $$('[data-gallery-thumb]', g);
      if (!main || !thumbs.length) return;
      thumbs.forEach(t => t.addEventListener('click', () => { main.src = t.dataset.src; main.srcset = t.dataset.srcset || ''; main.alt = t.dataset.alt || ''; thumbs.forEach(x => x.setAttribute('aria-current', 'false')); t.setAttribute('aria-current', 'true'); }));
    });
  }

  /* ---------- video poster (click-to-load) ---------- */
  function initVideo() {
    $$('[data-video-poster]').forEach(btn => {
      btn.addEventListener('click', () => {
        const wrap = btn.closest('[data-video]'); const src = wrap.dataset.src; const type = wrap.dataset.type;
        let el;
        if (type === 'html5') { el = document.createElement('video'); el.src = src; el.controls = true; el.autoplay = true; el.playsInline = true; el.setAttribute('aria-label', wrap.dataset.title || ''); }
        else { el = document.createElement('iframe'); el.src = src + (src.includes('?') ? '&' : '?') + 'autoplay=1&rel=0'; el.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'; el.allowFullscreen = true; el.title = wrap.dataset.title || 'Video'; el.loading = 'lazy'; }
        btn.replaceWith(el); if (el.focus) el.focus();
      });
    });
  }

  /* ---------- marquee pause button ---------- */
  function initMarquee() {
    $$('[data-marquee-pause]').forEach(btn => { const m = btn.closest('.marquee'); btn.addEventListener('click', () => { const paused = m.classList.toggle('is-paused'); $('.marquee__track', m).style.animationPlayState = paused ? 'paused' : 'running'; btn.setAttribute('aria-pressed', String(paused)); }); });
  }

  /* ---------- quotation page: suggested consumables & parts ---------- */
  function initRFQRecommend() {
    const wrap = $('[data-rfq-recommend]'); if (!wrap) return;
    const dataEl = $('[data-rfq-catalog]', wrap), list = $('[data-rfq-recommend-list]', wrap), tpl = $('[data-rfq-recommend-card]', wrap);
    if (!dataEl || !list || !tpl) return;
    let cat; try { cat = JSON.parse(dataEl.textContent); } catch (e) { return; }
    const max = parseInt(wrap.dataset.max, 10) || 4;
    const render = () => {
      const items = rfq.get(); const have = new Set(items.map(i => i.handle)); const fams = new Set();
      items.forEach(i => { const p = cat.products[i.handle]; if (!p) return; if (p.f) fams.add(p.f); (p.x || []).forEach(f => { if (f) fams.add(f); }); });
      const picks = [];
      ['consumable', 'part'].forEach(kind => Object.keys(cat.products).forEach(h => {
        const p = cat.products[h]; if (p.k !== kind || have.has(h) || picks.length >= max) return;
        if ((p.x || []).some(f => fams.has(f))) picks.push(Object.assign({ handle: h }, p));
      }));
      list.innerHTML = '';
      if (!picks.length) { wrap.hidden = true; return; }
      picks.forEach(p => {
        const node = tpl.content.cloneNode(true);
        $('[data-r-url]', node).href = p.u; $('[data-r-link]', node).href = p.u; $('[data-r-title]', node).textContent = p.t;
        const img = $('[data-r-img]', node); if (p.i) { img.src = p.i; img.alt = p.t; } else img.remove();
        $('[data-r-reason]', node).textContent = cat.reasons[p.k] || ''; $('[data-r-sku]', node).textContent = p.s || '';
        $('[data-r-add]', node).addEventListener('click', () => rfq.add({ handle: p.handle, title: p.t, url: p.u, image: p.i, sku: p.s || '' }));
        list.appendChild(node);
      });
      wrap.hidden = false;
    };
    render(); document.addEventListener('rfq:change', render);
  }

  /* ---------- document list filter ---------- */
  function initDocFilter() {
    $$('[data-doc-filter]').forEach(wrap => {
      const input = $('input', wrap); const list = wrap.closest('[data-doc-list]'); if (!input || !list) return;
      const items = $$('[data-doc]', list), groups = $$('[data-doc-group]', list), none = $('[data-doc-none]', list);
      input.addEventListener('input', () => {
        const q = input.value.trim().toLowerCase(); let n = 0;
        items.forEach(it => { const on = !q || it.dataset.doc.includes(q); it.hidden = !on; if (on) n++; });
        groups.forEach(g => { g.hidden = !$$('[data-doc]', g).some(it => !it.hidden); });
        if (none) none.hidden = n > 0;
      });
    });
  }

  /* ---------- forms: native validation messaging ---------- */
  function initForms() {
    $$('form[data-validate]').forEach(form => {
      form.addEventListener('submit', (e) => {
        let ok = true;
        $$('[required]', form).forEach(f => { const bad = !f.checkValidity(); f.setAttribute('aria-invalid', String(bad)); const err = f.parentElement.querySelector('.field__error'); if (err) err.hidden = !bad; if (bad) ok = false; });
        if (!ok) { e.preventDefault(); const first = $('[aria-invalid="true"]', form); if (first) first.focus(); }
      });
      $$('[required]', form).forEach(f => f.addEventListener('input', () => { if (f.checkValidity()) { f.setAttribute('aria-invalid', 'false'); const err = f.parentElement.querySelector('.field__error'); if (err) err.hidden = true; } }));
    });
    // Scroll to form status after Shopify redirects back with ?contact_posted or #errors
    const status = $('.form-message'); if (status && (location.hash.includes('contact') || location.search.includes('contact_posted') || location.hash.includes('newsletter'))) status.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  function init() {
    if (window.themeSettings && window.themeSettings.smoothScroll) document.documentElement.classList.add('smooth-scroll');
    initHeader(); initDrawers(); initSearch(); initSliders(); initLightbox(); initReveal(); initBackToTop(); initCookies(); initRFQ(); initQuantity(); initProductGallery(); initVideo(); initMarquee(); initForms(); initDocFilter(); initRFQRecommend();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  // Re-init in the theme editor when sections load
  document.addEventListener('shopify:section:load', () => { initSliders(); initLightbox(); initVideo(); initRFQ(); initProductGallery(); initMarquee(); initHeader(); initDrawers(); $$('.reveal').forEach(e => e.classList.add('is-visible')); });
})();
